import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Component } from 'react';
import { toast } from 'react-toastify';
import { Dispatch, connect } from "react-redux";
import { IDonorProfileDonorData, IDonorProfileDonorDataLookupData, IDonorProfileDonorDataPost, IDonorProfileHeaderInfo } from '@nkr/models/index';
import { DonationType, RemoteEvalStatus, MatchStatus, DonorType, DonationTypeDescription, DonorProfileType, isVoucher } from '@nkr/enums/index';
import {
    IApplicationState, AppActionCreators, IAppState,
    DonorActionCreators, IDonorProfileDonorDataResponse, IDonorProfileDonorDataLookupDataResponse, DonorSwapActionCreators, IDonorAliasDuplicateCheckResponse,
} from '@nkr/store/index';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';
import { IBaseDispatchProps, ToggleType } from '../models';
import { IValidation } from './validation';
import DonorDataContent from './content';
import DonorDataPairedContent from './content-paired';
import DonorDataContentSelfRegistered from './content-self-registered';

interface IOwnProps {
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    donorId: number,
    headerInfo: IDonorProfileHeaderInfo,
    hasDataChanged: boolean,
    saveRequested: boolean,
    refreshRequested: boolean,
    onDataChanged: (hasDataChanged: boolean) => void,
    onSaveFinished: (refreshHeader: boolean) => void,
    onRefreshFinished: () => void,
    onResetDataChangedWarning: () => void,
    onTabClick: (href: string) => void,
    onUpdateForErrors: (toggleType: ToggleType, messages: string[]) => void,
}

interface IStateProps {
    app: IAppState,
    groupIsSuper: boolean,
    groupIsCenter: boolean
}

interface IDispatchProps extends IBaseDispatchProps {
    getDonorData: (id: number, centerId: number) => Promise<IDonorProfileDonorDataResponse>,
    getDonorDataLookupData: (id: number, centerId: number) => Promise<IDonorProfileDonorDataLookupDataResponse>,
    postProfileDonorData: (id: number, centerId: number, data: IDonorProfileDonorDataPost) => Promise<IDonorProfileDonorDataResponse>,
    checkAlias: (donorId: number, alias: string) => Promise<IDonorAliasDuplicateCheckResponse>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    data: IDonorProfileDonorData,
    adminComments: string,
    originalData: IDonorProfileDonorData,
    lookupData: IDonorProfileDonorDataLookupData,
    donorDataHash: string,
    dirty: boolean,
    validation: IValidation,
    showDuplicateRecord: boolean,
    duplicateMessage: string
}

const initialState: IState = {
    data: null,
    adminComments: '',
    originalData: null,
    lookupData: null,
    donorDataHash: '',
    dirty: false,
    validation: {
        alias: true,
        donationStartDate: true,
        donationEndDate: true
    },
    showDuplicateRecord: false,
    duplicateMessage: ''
}

class DonorDataTab extends Component<TProps, IState> {
    readonly state: IState = initialState;

    componentDidMount = () => {
        this.getData();
    }

    componentWillReceiveProps = (nextProps: Readonly<TProps>, nextContext: any) => {
        if (this.props.saveRequested == false && nextProps.saveRequested == true) {
            this.handleValidate();
        }

        if (this.props.refreshRequested == false && nextProps.refreshRequested == true) {
            this.getData();
        }
    }

    getData = () => {
        let { centerId, donorId } = this.props;

        this.props.showLoading('Getting donor data, one moment please...').then(() => {
            Promise.all([
                this.props.getDonorData(centerId, donorId),
                this.props.getDonorDataLookupData(donorId, centerId)
            ])
                .then(([dataResponse, lookupDataResponse]) => {
                    if (dataResponse.data.success && lookupDataResponse.data.success) {
                        let data = dataResponse.data.results;
                        let donorDataHash = hash(data);

                        this.setState({
                            data,
                            originalData: data,
                            lookupData: lookupDataResponse.data.results,
                            donorDataHash,
                        }, () => {
                            if (this.props.refreshRequested) {
                                this.props.onRefreshFinished();
                            }
                        });
                    }
                })
                .catch(error => {
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    updateValidation = () => {
        let messages: string[] = []

        let validation: IValidation = {
            alias: true,
            donationStartDate: true,
            donationEndDate: true
        };

        let { data, originalData } = this.state;

        if (!data.mrn) {
            messages.push("MRN cannot be blank");
        }

        let regex = new RegExp('^[a-zA-Z0-9]{6,12}$');

        if (!regex.test(data.alias)) {
            messages.push("Alias must contain only letters and numbers and be between 6 to 12 characters")
            validation.alias = false;
        } else {
            validation.alias = true;
        }

        var isNewReferral = (data.donorType == DonorType.NonDirectedDonor && !originalData.centerId && data.centerId)

        if (isNewReferral && !data.isActive) {
            messages.push('Donor must be active to be referred');
        }

        if (data.matchStatus != MatchStatus.Transplanted && !(data.isActive == false || !data.isDirect)) {
            messages.push(`You cannot set a ${DonationTypeDescription[data.donationType]} donor 'active' for matching`)
        }

        if (data.donationStartDate && data.donationEndDate) {
            let start = moment(data.donationStartDate, "MM/DD/YYYY", true);
            let end = moment(data.donationEndDate, "MM/DD/YYYY", true);
            if (start.isValid() && end.isValid()) {
                if (start > end)
                    messages.push(`Latest donation date cannot be before Earliest donation date.`)
            }


            if (data.isChainStarter) {

                const today = moment(); // newStartDate is really today
                const earliestAllowedStart = this.addBusinessDays(today, 15);

                let existingStart = moment(originalData.donationStartDate);
                let existingEnd = moment(originalData.donationEndDate);
                let newStart = moment(data.donationStartDate);
                let newEnd = moment(data.donationEndDate);


                let isChange =
                    (!originalData.isActive && data.isActive) ||
                    !existingStart.isSame(newStart) ||
                    !existingEnd.isSame(newEnd);

                const formattedEarliestDate = earliestAllowedStart.format("MM/DD/YYYY");

                if (data.donationType == DonationType.StandardVoucher && isChange && !this.props.groupIsSuper) {

                    const businessDaysFromToday = this.getBusinessDays(today, newStart);

                    if (businessDaysFromToday < 15) {
                        messages.push(
                            `Donation range start date must be at least 15 business days from today (${formattedEarliestDate}).`
                        );
                        validation.donationStartDate = false;
                    }

                    const businessDaysBetweenStartEnd = this.getBusinessDays(newStart, newEnd);

                    if (businessDaysBetweenStartEnd < 15 || businessDaysBetweenStartEnd > 30) {
                        messages.push(
                            "Donation ranges must span a minimum of 15 business days and a maximum of 30 business days for SV donors."
                        );
                        validation.donationEndDate = false;
                    }
                }

                if ((data.donationType == DonationType.NonDirectedDonor || data.donationType == DonationType.FamilyVoucher) && isChange && !this.props.groupIsSuper) {

                    const businessDaysBetweenStartEnd = this.getBusinessDays(newStart, newEnd);

                    if (businessDaysBetweenStartEnd < 5 || businessDaysBetweenStartEnd > 15) {
                        messages.push(
                            "Donation ranges must span a minimum of 5 business days and a maximum of 15 business days for NDD/FV donors."
                        );
                        validation.donationStartDate = false;
                    }
                }
            }


            if (data.isActive && data.matchStatus < MatchStatus.Transplanted && data.donorType == DonorType.NonDirectedDonor && !this.props.groupIsSuper) {
                let existingStart = moment(originalData.donationStartDate).clone();
                let existingEnd = moment(originalData.donationEndDate).clone();
                let newStart = moment(data.donationStartDate).clone();
                let newEnd = moment(data.donationEndDate).clone();

                if (!existingStart.isSame(newStart) || !existingEnd.isSame(newEnd)) {
                    if (newStart.isValid() == false) {
                        messages.push('Donation start date must be today or later');
                        validation.donationStartDate = false;
                    }

                    if (newEnd.isValid()) {
                        if (newEnd.isBefore(newStart)) {
                            messages.push('Donation end date must be empty or greater than/equal to start date');
                            validation.donationEndDate = false;
                        }
                    }
                }
            }

            //Checks to see if input is past 6 weeks range, if so, set back to old data for voucher donors
            if (isVoucher(this.state.data.donationType)) {
                let startDate = moment(this.state.data.donationStartDate).clone();
                let endDate = moment(this.state.data.donationEndDate).clone();

                if (startDate.add(6, 'weeks').isBefore(endDate)) {
                    messages.push('Donation range must be no more than 42 days total');

                    validation.donationStartDate = false;
                    validation.donationEndDate = false;
                }
            }
        }

        if (originalData.remoteWorkupBilledDate != data.remoteWorkupBilledDate) {
            if (data.centerId != this.props.centerId && !originalData.remoteWorkupDate) {
                let isRemoteWorkUpBillable = (originalData.didRemoteWorkup == RemoteEvalStatus.YesWillNotDonate && data.remoteWorkupDate != '' && data.remoteWorkupDate != null);

                if (isRemoteWorkUpBillable) {
                    let remoteWorkupDate = moment(data.remoteWorkupDate);
                    let cutoff = moment('2022-07-01');

                    if (remoteWorkupDate.isAfter(cutoff)) {
                        isRemoteWorkUpBillable = false;
                        data.remoteWorkupDate = '';
                        data.remoteWorkupCenterId = null;
                        data.remoteWorkupBilledDate = '';

                        messages.push('No longer bilable after 7/1/2022, will be billed upon nephrectomy');
                    }
                }

                if (isRemoteWorkUpBillable) {
                    let remoteWorkupDate = moment(data.remoteWorkupDate);
                    let lastDayOfMonth = moment().endOf('month');

                    data.remoteWorkupBilledDate = lastDayOfMonth.format('MM/DD/YY');

                    if (remoteWorkupDate.isValid()) {
                        data.remoteWorkupCenterId = data.rdnCenterId;

                        let remoteWorkupDateFormat = moment(data.remoteWorkupDate);

                        remoteWorkupDateFormat = remoteWorkupDateFormat.add(270, 'day');

                        if (!this.props.isAdmin && lastDayOfMonth.isAfter(remoteWorkupDateFormat)) {
                            data.remoteWorkupDate = '';
                            data.remoteWorkupCenterId = null;
                            data.remoteWorkupBilledDate = '';

                            messages.push('Outside of 270-day invoicing range');
                        }
                    }
                }
            }
        }

        return { data, validation, messages };
    }

    save = () => {
        let { adminComments } = this.state;

        let { alias, comments } = this.state.data;

        if (adminComments) {
            comments +=
                `<div class='notes'>
                    <div class='small_descr' style='float:right;'><b>${moment(new Date()).format('MM/DD/YYYY HH:mm')}</b></div>
                    <div class='heading'>NOTE BY: '${this.props.app.user.username}'</div>
                    <div class='note'>${adminComments}</div>
                </div>`
        }

        let newDataPost: IDonorProfileDonorDataPost = {
            ...this.state.data,
            comments,
        };

        this.props.checkAlias(this.props.donorId, alias).then(response => {
            if (!response.data.success) {
                return toast.error("Alias already exists, please create a new one...")
            }

            this.props.showLoading("Saving Donor Data, one moment please...").then(() => {
                this.props.postProfileDonorData(this.props.donorId, this.props.centerId, newDataPost)
                    .then(response => {
                        if (response.data.success) {
                            let donorDataHash = hash(this.state.data);

                            this.setState({
                                donorDataHash,
                            }, () => {
                                this.getData();

                                this.props.onDataChanged(false);
                                this.props.onResetDataChangedWarning();
                                this.props.onUpdateForErrors(ToggleType.Success, []);
                                this.props.onSaveFinished(true);
                            })
                        }
                    })
                    .catch((error: any) => {
                        if (error.response.status == 409) {
                            this.setState({
                                showDuplicateRecord: true,
                                duplicateMessage: error.response.data
                            }, () => {
                                this.props.onSaveFinished(false);
                            });

                            return;
                        }

                        toast.error(error.response.data);
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        }).catch((error: any) => {
            toast.error('Unable to check if Alias already exists:' + error.response.data);
        })
    }

    handleDonorDataChange = (data: IDonorProfileDonorData, checkDirty: boolean = true) => {
        let newData = _.cloneDeep(data);
        let newHash = hash(newData);

        let hasDataChanged = checkDirty ? newHash != this.state.donorDataHash || this.state.adminComments != '' : false;

        this.setState({
            data: newData,
        }, () => {
            if (this.state.dirty) {
                let { data, validation, messages } = this.updateValidation();

                this.setState({
                    data,
                    validation,
                }, () => {
                    this.props.onUpdateForErrors(ToggleType.Errors, messages);
                    this.props.onSaveFinished(false);
                });
            }
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleAdminCommentChange = (value: string) => {
        let newData = _.cloneDeep(this.state.data);
        let newHash = hash(newData);

        let hasDataChanged = newHash != this.state.donorDataHash || value != '';

        this.setState({
            adminComments: value,
        });

        this.props.onDataChanged(hasDataChanged);
    }

    handleValidate = () => {
        let { data, validation, messages } = this.updateValidation();

        if (messages.length == 0) {
            this.setState({
                data,
                validation,
                dirty: true
            }, () => {
                this.save();
            });
        } else {
            this.setState({
                data,
                validation,
                dirty: true
            }, () => {
                this.props.onUpdateForErrors(ToggleType.Errors, messages);
                this.props.onSaveFinished(false);
            });
        }
    }

    getBusinessDays = (startDate, endDate) => {
        let start = moment(startDate).startOf('day');
        let end = moment(endDate).startOf('day');

        if (end.isBefore(start)) {
            [start, end] = [end, start];
        }

        let businessDays = 0;

        while (start.isSameOrBefore(end)) {
            const day = start.day();
            if (day !== 0 && day !== 6) { // 0 = Sunday, 6 = Saturday
                businessDays++;
            }
            start.add(1, 'day');
        }

        return businessDays;
    }


    addBusinessDays = (datePassed: moment.MomentInput, xAmountDays: number) => {
        let date = moment(datePassed);
        let addedDays = 0;

        while (addedDays < xAmountDays) {
            date = date.add(1, "day");

            const day = date.day();
            if (day !== 0 && day !== 6) {
                addedDays++;
            }
        }

        return date.subtract(1, "day");
    }

    handleCloseDuplicateRecord = () => {
        this.setState({
            showDuplicateRecord: false
        });
    }

    render = () => {
        let { data, lookupData } = this.state;

        if (data == null || lookupData == null || this.props.headerInfo == null) {
            return null;
        }

        let { contactList, countryList, stateList, declineAdpReasonList, relationshipToPatientList, remoteEvalStatusList, centerList } = lookupData;

        let coordinatorList = _.sortBy(contactList.filter(x => x.isCoordinator), item => item.fullName);
        let surgeonList = _.sortBy(contactList.filter(x => x.isSurgeon), item => item.fullName);

        return (
            <>
                {data.viewType == DonorProfileType.EditSelfRegistered &&
                    <DonorDataContentSelfRegistered
                        centerId={this.props.centerId}
                        data={data}
                        surgeonList={surgeonList}
                        isSuperAdmin={this.props.groupIsSuper}
                        isCenter={this.props.groupIsCenter}
                        validation={this.state.validation}
                        coordinatorList={coordinatorList}
                        countryList={countryList}
                        stateList={stateList}
                        declineAdpReasonList={declineAdpReasonList}
                        relationshipToPatientList={relationshipToPatientList}
                        remoteEvalStatus={remoteEvalStatusList}
                        rdnCenterList={centerList}
                        onChange={this.handleDonorDataChange}
                    />
                }

                {data.viewType == DonorProfileType.Edit &&
                    <DonorDataContent
                        centerId={this.props.centerId}
                        data={data}
                        adminComments={this.state.adminComments}
                        isSuperAdmin={this.props.groupIsSuper}
                        isCenter={this.props.groupIsCenter}
                        validation={this.state.validation}
                        surgeonList={surgeonList}
                        coordinatorList={coordinatorList}
                        countryList={countryList}
                        stateList={stateList}
                        declineAdpReasonList={declineAdpReasonList}
                        relationshipToPatientList={relationshipToPatientList}
                        remoteEvalStatus={remoteEvalStatusList}
                        rdnCenterList={centerList}
                        onChange={this.handleDonorDataChange}
                        onAdminChange={this.handleAdminCommentChange}
                    />
                }

                {data.viewType == DonorProfileType.EditPaired &&
                    <DonorDataPairedContent
                        centerId={this.props.centerId}
                        data={data}
                        adminComments={this.state.adminComments}
                        isSuperAdmin={this.props.groupIsSuper}
                        isCenter={this.props.groupIsCenter}
                        validation={this.state.validation}
                        surgeonList={surgeonList}
                        coordinatorList={coordinatorList}
                        countryList={countryList}
                        stateList={stateList}
                        declineAdpReasonList={declineAdpReasonList}
                        relationshipToPatientList={relationshipToPatientList}
                        remoteEvalStatus={remoteEvalStatusList}
                        rdnCenterList={centerList}
                        onChange={this.handleDonorDataChange}
                        onAdminChange={this.handleAdminCommentChange}
                    />
                }

                <DuplicateRecordModal
                    show={this.state.showDuplicateRecord}
                    message={this.state.duplicateMessage}
                    onClose={this.handleCloseDuplicateRecord}
                />
            </>
        )
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
        groupIsSuper: appState.app.user.group.isSuper,
        groupIsCenter: appState.app.user.group.isCenter
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getDonorData: (centerId, id) => {
            return dispatch(DonorActionCreators.getProfileDonorData(id, centerId));
        },
        getDonorDataLookupData: (id, centerId) => {
            return dispatch(DonorActionCreators.getProfileDonorDataLookupData(id, centerId));
        },
        postProfileDonorData: (id, centerId, data) => {
            return dispatch(DonorActionCreators.postProfileDonorData(id, centerId, data))
        },
        checkAlias: (donorId, alias) => {
            return dispatch(DonorSwapActionCreators.checkAlias(donorId, alias))
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(DonorDataTab);
